package cz.lhoracek.mapl.ui.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.w3c.dom.events.Event
import kotlin.js.json

private const val HISTORY_DEPTH_KEY = "__maplDepth"

@Composable
actual fun SyncNavigationWithBrowserHistory(
    stackDepth: Int,
    onBackFromBrowser: () -> Unit,
) {
    val normalizedDepth = stackDepth.coerceAtLeast(1)
    val handleBrowserBack by rememberUpdatedState(onBackFromBrowser)
    var previousDepth by remember { mutableIntStateOf(normalizedDepth) }
    var skipNextDepthSync by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        if (readHistoryDepth() == null) {
            replaceHistoryDepth(normalizedDepth)
        }
        previousDepth = normalizedDepth

        val listener: (Event) -> Unit = listener@{
            val targetDepth = readHistoryDepth() ?: return@listener
            if (targetDepth < previousDepth) {
                repeat(previousDepth - targetDepth) { handleBrowserBack() }
                skipNextDepthSync = true
            } else if (targetDepth > previousDepth) {
                // Forward navigation is currently not represented in app state.
                skipNextDepthSync = true
            }
            previousDepth = targetDepth
        }

        window.addEventListener("popstate", listener)
        onDispose {
            window.removeEventListener("popstate", listener)
        }
    }

    LaunchedEffect(normalizedDepth) {
        if (skipNextDepthSync) {
            skipNextDepthSync = false
            previousDepth = normalizedDepth
            return@LaunchedEffect
        }

        when {
            normalizedDepth > previousDepth -> {
                repeat(normalizedDepth - previousDepth) { index ->
                    pushHistoryDepth(previousDepth + index + 1)
                }
            }

            normalizedDepth == previousDepth && readHistoryDepth() == null -> {
                replaceHistoryDepth(normalizedDepth)
            }
        }

        previousDepth = normalizedDepth
    }
}

actual fun requestPlatformBack(onBack: () -> Unit) {
    val depth = readHistoryDepth() ?: 1
    if (depth > 1) {
        window.history.back()
    } else {
        onBack()
    }
}

private fun readHistoryDepth(): Int? {
    val state = window.history.state ?: return null
    val raw = state.asDynamic()[HISTORY_DEPTH_KEY]
    return (raw as? Number)?.toInt()
}

private fun pushHistoryDepth(depth: Int) {
    window.history.pushState(json(HISTORY_DEPTH_KEY to depth), "", window.location.href)
}

private fun replaceHistoryDepth(depth: Int) {
    window.history.replaceState(json(HISTORY_DEPTH_KEY to depth), "", window.location.href)
}
